import React from "react";
import {
  LoginPageProps,
  LoginFormTypes,
  useLink,
  useRouterType,
  useActiveAuthProvider,
  useLogin,
  useTranslate,
  useRouterContext,
} from "@refinedev/core";
import { ThemedTitleV2 } from "@refinedev/antd";
import {
  bodyStyles,
  containerStyles,
  headStyles,
  layoutStyles,
  titleStyles,
} from "./styles";
import {
  Row,
  Col,
  Layout,
  Card,
  Typography,
  Form,
  Input,
  Button,
  Checkbox,
  CardProps,
  LayoutProps,
  Divider,
  FormProps,
  theme,
  message,
  Alert,
} from "antd";
import { PageTitle } from "./components/PageTitle";
import { AuthHeroPanel } from "./components/AuthHeroPanel";
import ReCAPTCHA from "react-google-recaptcha";
import { isCaptchaEnabled } from "../../shared/captcha";
import { isTenantSignupEnabled } from "../../shared/tenantSignup";

type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

/**
 * Login refusals that the user cannot fix by retyping their password. These get a
 * persistent Alert with a recovery path rather than a transient toast.
 */
const BLOCKING_CODES = [
  "tenantNotActivated",
  "trialExpired",
  "tenantSuspended",
  "userNotActivated",
  "accountNotActive",
];

/** The two an activation link can resolve. */
const RESENDABLE_CODES = ["tenantNotActivated", "userNotActivated"];
/**
 * **refine** has a default login page form which is served on `/login` route when the `authProvider` configuration is provided.
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/antd-auth-page/#login} for more details.
 */
export const LoginPage: React.FC<LoginProps> = ({
  providers,
  registerLink,
  forgotPasswordLink,
  rememberMe,
  contentProps,
  wrapperProps,
  renderContent,
  formProps,
  title,
  hideForm,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<LoginFormTypes>();
  const translate = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();

  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;
  const [failedLogin, setFailedLogin] = React.useState(0);
  const [showRecaptcha, setShowRecaptcha] = React.useState(false);
  const [recaptchaValue, setRecaptchaValue] = React.useState<string | null>(null);
  const [blockedCode, setBlockedCode] = React.useState<string | null>(null);

  // A mid-session tenant block bounces the user here with ?blocked=<code> so the
  // explanation survives the redirect (see the 403 handler in authProvider).
  React.useEffect(() => {
    const blocked = new URLSearchParams(window.location.search).get("blocked");
    if (blocked && BLOCKING_CODES.includes(blocked)) {
      setBlockedCode(blocked);
    }
  }, []);

  const authProvider = useActiveAuthProvider();
  const { mutate: login, isLoading } = useLogin<LoginFormTypes>({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  
  /**
   * Blocking refusals (not activated, trial over, suspended) get a persistent Alert —
   * they need a link the user can actually click, which a 3s toast cannot offer.
   * A wrong password stays a toast, since retrying is the whole fix.
   */
  const handleLoginFailure = (code: string | undefined, fallback?: string) => {
    if (code && BLOCKING_CODES.includes(code)) {
      setBlockedCode(code);
      return;
    }
    setBlockedCode(null);
    message.error(
      fallback ??
      translate("pages.login.errors.invalid", "Invalid email or password")
    );
    setFailedLogin((prev) => {
      const next = prev + 1;
      if (isCaptchaEnabled && next > 1) {
        setShowRecaptcha(true);
      }
      return next;
    });
  };

  const onSubmit = (values: LoginFormTypes) => {
    if (isCaptchaEnabled && showRecaptcha && !recaptchaValue) {
      return;
    }
    setBlockedCode(null);
    login(values,
      {
        onSuccess: (data) => {
          const resp: any = data as any;
          if (!resp?.success || resp?.error) {
            handleLoginFailure(resp?.error?.name, resp?.error?.message);
          }
        },
        onError: (error) => {
          handleLoginFailure((error as any)?.name, (error as any)?.message);
        },
      },
    );
  }

  const onRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    console.log("Captcha value:", value);
  }

  const renderProviders = () => {
    if (providers && providers.length > 0) {
      return (
        <>
          {providers.map((provider) => {
            return (
              <Button
                key={provider.name}
                type="default"
                block
                icon={provider.icon}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  marginBottom: "8px",
                }}
                onClick={() =>
                  onSubmit({
                    providerName: provider.name,
                  })
                }
              >
                {provider.label}
              </Button>
            );
          })}
          {!hideForm && (
            <Divider>
              <Typography.Text
                style={{
                  color: token.colorTextLabel,
                }}
              >
                {translate("pages.login.divider", "or")}
              </Typography.Text>
            </Divider>
          )}
        </>
      );
    }
    return null;
  };

  const CardContent = (
    <Card
      headStyle={headStyles}
      bodyStyle={bodyStyles}
      style={{
        ...containerStyles,
        backgroundColor: token.colorBgContainer,
        borderColor: token.colorBorder,
      }}
      bordered={false}
      {...(contentProps ?? {})}
    >
      <div className="mb-12">
        <Typography.Title level={1} className="text-left" style={{ color: token.colorText }}>
          {translate("pages.login.title", "Sign in to your account")}
        </Typography.Title>
      </div>
      {renderProviders()}
      {blockedCode && (
        <Alert
          type={
            blockedCode === "trialExpired" || blockedCode === "tenantSuspended"
              ? "error"
              : "warning"
          }
          showIcon
          style={{ marginBottom: 16 }}
          message={translate(`pages.login.blocked.${blockedCode}.title`)}
          description={
            <>
              {translate(`pages.login.blocked.${blockedCode}.description`)}
              {RESENDABLE_CODES.includes(blockedCode) && (
                <>
                  {" "}
                  <ActiveLink
                    to={`/resend-activation?email=${encodeURIComponent(
                      form.getFieldValue("email") ?? ""
                    )}`}
                    style={{
                      fontWeight: "bold",
                      color: token.colorPrimaryTextHover,
                    }}
                  >
                    {translate("pages.login.blocked.resendLink")}
                  </ActiveLink>
                </>
              )}
            </>
          }
        />
      )}
      {!hideForm && (
        <Form<LoginFormTypes>
          layout="vertical"
          form={form}
          onFinish={(values) => onSubmit(values)}
          requiredMark={false}
          initialValues={{
            remember: false,
          }}
          {...formProps}
        >
          <Form.Item
            name="email"
            label={translate("pages.login.fields.email", "Email")}
            rules={[
              {
                required: true,
                message: translate("pages.login.errors.validEmail")
              },
              {
                type: "email",
                message: translate("pages.login.errors.validEmail"),
              },
            ]}
          >
            <Input
              size="large"
              placeholder={translate("pages.login.fields.email")}
              style={{
                backgroundColor: token.colorBgContainer,
                borderColor: token.colorBorder,
                color: token.colorText,
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={translate("pages.login.fields.password")}
            rules={[{ required: true, message: translate("forms.errors.required") }]}
          >
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="*******"
              size="large"
              style={{
                backgroundColor: token.colorBgContainer,
                borderColor: token.colorBorder,
                color: token.colorText,
              }}
            />
          </Form.Item>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            {rememberMe ?? (
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <div className="flex flex-col gap-0.5">
                  <Checkbox
                    autoComplete="on"
                    style={{
                      fontSize: "12px",
                      color: token.colorText,
                    }}
                  >
                    {translate("pages.login.buttons.rememberMe", "Remember me")}
                  </Checkbox>
                  <Typography.Text
                    className="text-xs opacity-60"
                    style={{ color: token.colorTextSecondary }}
                  >
                    {translate(
                      "pages.login.hints.rememberMe",
                      "Stay signed in for 30 days",
                    )}
                  </Typography.Text>
                </div>
              </Form.Item>
            )}
            {forgotPasswordLink ?? (
              <ActiveLink
                style={{
                  color: token.colorPrimaryTextHover,
                  fontSize: "12px",
                  marginLeft: "auto",
                }}
                to="/forgot-password"
              >
                {translate(
                  "pages.login.buttons.forgotPassword",
                  "Forgot password?"
                )}
              </ActiveLink>
            )}
          </div>
          {!hideForm && (
            <Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={isLoading}
                className="shadow-none"
                block
              >
                {translate("pages.login.signin", "Sign in")}
              </Button>
            </Form.Item>
          )}
        </Form>
      )}

      {registerLink ?? (
        <div
          style={{
            marginTop: hideForm ? 16 : 8,
          }}
        >
          <Typography.Text style={{ fontSize: 12, color: token.colorText }}>
            {translate(
              "pages.login.buttons.noAccount",
              "Don't have an account?"
            )}{" "}
            <ActiveLink
              to="/register"
              style={{
                fontWeight: "bold",
                color: token.colorPrimaryTextHover,
              }}
            >
              {translate("pages.login.signup", "Sign up")}
            </ActiveLink>
          </Typography.Text>
          {isTenantSignupEnabled && (
            <div style={{ marginTop: 4 }}>
              <ActiveLink
                to="/signup"
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: token.colorPrimaryTextHover,
                }}
              >
                {translate("pages.login.buttons.createWorkspace")}
              </ActiveLink>
            </div>
          )}
        </div>
      )}
      {isCaptchaEnabled && showRecaptcha && (
        <ReCAPTCHA
          sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
          onChange={onRecaptchaChange}
        />
      )}
    </Card>
  );

  const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";

  return (
    <Layout style={{ ...layoutStyles, backgroundColor: token.colorBgLayout, position: "relative" }} {...(wrapperProps ?? {})}>
      <Row style={{ minHeight: "100dvh" }}>
        <Col
          xs={24}
          lg={10}
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: token.colorBgContainer,
          }}
        >
          {renderContent ? (
            renderContent(CardContent, <PageTitle />)
          ) : (
            <div style={{ maxWidth: 400, width: "100%" }}>
              <PageTitle />
              {CardContent}
            </div>
          )}
        </Col>
        <AuthHeroPanel />
      </Row>
      <Typography.Text
        className="absolute bottom-4 left-4 text-xs opacity-60"
        style={{
          color: token.colorTextSecondary,
          fontSize: "12px",
        }}
      >
        v{appVersion}
      </Typography.Text>
    </Layout>
  );
};
